import React, { createContext, useContext, useEffect, useState } from 'react';
import { User as SupabaseUser, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User, UserPreferences } from '../lib/database.types';

// Define the userData type for signup
interface SignUpUserData {
  phoneNumber: string;
  fullName: string;
  timezone: string;
  morningTime: string;
  eveningTime: string;
  weekendMessaging: boolean;
  responseMethod: 'text' | 'voice' | 'both';
  goalVisibility: 'private' | 'public';
  hasAccountabilityPartner: boolean;
  partnerEmail: string;
  partnerName: string;
  hasPermission: boolean;
}

// Define error response type
interface AuthErrorResponse {
  error: AuthError | Error | null;
  data?: { user: SupabaseUser } | null;
}

interface AuthContextType {
  user: SupabaseUser | null;
  userProfile: User | null;
  userPreferences: UserPreferences | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: SignUpUserData) => Promise<{
    error: AuthError | Error | null,
    data: { user: SupabaseUser | null, session: Session | null } | null
  }>;
  signIn: (email: string, password: string) => Promise<AuthErrorResponse>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthErrorResponse>;
  updateUserProfile: (updates: Partial<User>) => Promise<AuthErrorResponse>;
  updateUserPreferences: (updates: Partial<UserPreferences>, userId?: string) => Promise<AuthErrorResponse>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userProfile, setUserProfile] = useState<User | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userPreferences, setUserPreferences] = useState<UserPreferences | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialSessionProcessed, setInitialSessionProcessed] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('[AuthProvider] Getting initial session...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AuthProvider] Error getting session:', error);
          if (mounted) {
            setLoading(false);
            setInitialSessionProcessed(true);
          }
          return;
        }
        
        if (mounted) {
          console.log('[AuthProvider] Initial session:', session?.user?.id || 'no session');
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            console.log('[AuthProvider] User found in initial session, fetching data...');
            await fetchUserData(session.user.id);
          }
          
          // Always set loading to false after processing initial session
          console.log('[AuthProvider] Setting loading to false after initial session');
          setLoading(false);
          setInitialSessionProcessed(true);
        }
      } catch (error) {
        console.error('[AuthProvider] Error in getInitialSession:', error);
        if (mounted) {
          console.log('[AuthProvider] Error occurred, setting loading to false');
          setLoading(false);
          setInitialSessionProcessed(true);
        }
      }
    };

    getInitialSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthProvider] Auth state changed:', event, session?.user?.id);
      
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Handle PASSWORD_RECOVERY event as per official documentation
        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthProvider] Password recovery event detected');
          // The user is now authenticated and can update their password
          // We don't need to do anything special here - just let the component handle it
        }
        
        // Handle USER_UPDATED event (triggered after password update)
        if (event === 'USER_UPDATED') {
          console.log('[AuthProvider] User updated event detected');
          
          // The user's password has been updated, redirect to dashboard
          // Check if we're on the password reset page and redirect
          if (window.location.pathname === '/reset-password') {
            console.log('[AuthProvider] Redirecting from password reset page to dashboard');
            window.location.href = '/';
          }
        }
        
        // Log user state changes
        console.log('[AuthProvider] User state updated:', { 
          event, 
          userId: session?.user?.id || 'null',
          hasSession: !!session 
        });
        
        // Only fetch user data for subsequent auth changes, not the initial one
        if (initialSessionProcessed && event !== 'INITIAL_SESSION') {
          if (session?.user) {
            console.log('[AuthProvider] User logged in, fetching data...');
            await fetchUserData(session.user.id);
          } else {
            console.log('[AuthProvider] User logged out, clearing data...');
            setUserProfile(null);
            setUserPreferences(null);
          }
        }
        
        // Only set loading to false if initial session has been processed
        // and this is not the initial session load
        if (initialSessionProcessed && event !== 'INITIAL_SESSION') {
          console.log('[AuthProvider] Setting loading to false after auth state change');
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [initialSessionProcessed]);

  const fetchUserData = async (userId: string, retryCount = 0) => {
    console.log('[AuthProvider] fetchUserData called for:', userId, 'retry:', retryCount);
    
    try {
      console.log('[AuthProvider] Starting to fetch user data...');

      // Fetch user profile first
      console.log('[AuthProvider] Fetching user profile...');
      const profileStartTime = Date.now();
      
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      const profileEndTime = Date.now();
      console.log('[AuthProvider] Profile fetch completed in', profileEndTime - profileStartTime, 'ms:', { profile: !!profile, error: profileError });
      
      if (profileError) {
        console.error('[AuthProvider] Profile error:', profileError);
        if (profileError.code === 'PGRST116') {
          console.log('[AuthProvider] Profile not found (404)');
          // Retry after a delay if this is the first attempt and we're within a reasonable time window
          if (retryCount < 2) {
            console.log('[AuthProvider] Retrying profile fetch in 1 second...');
            setTimeout(() => fetchUserData(userId, retryCount + 1), 1000);
            return;
          }
        }
      } else {
        console.log('[AuthProvider] User profile fetched successfully:', !!profile);
        setUserProfile(profile);
      }

      // Fetch user preferences second
      console.log('[AuthProvider] Fetching user preferences...');
      const preferencesStartTime = Date.now();
      
      const { data: preferences, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      const preferencesEndTime = Date.now();
      console.log('[AuthProvider] Preferences fetch completed in', preferencesEndTime - preferencesStartTime, 'ms:', { preferences: !!preferences, error: preferencesError });
      
      if (preferencesError) {
        console.error('[AuthProvider] Preferences error:', preferencesError);
        if (preferencesError.code === 'PGRST116') {
          console.log('[AuthProvider] Preferences not found (404), will be created by trigger');
          // Retry after a delay if this is the first attempt and we're within a reasonable time window
          if (retryCount < 2) {
            console.log('[AuthProvider] Retrying preferences fetch in 1 second...');
            setTimeout(() => fetchUserData(userId, retryCount + 1), 1000);
            return;
          }
        }
      } else {
        console.log('[AuthProvider] User preferences fetched successfully:', !!preferences);
        setUserPreferences(preferences);
      }
      
      console.log('[AuthProvider] User data fetch completed');
    } catch (error) {
      console.error('[AuthProvider] Error fetching user data:', error);
    }
  };

  const signUp = async (email: string, password: string, userData: SignUpUserData): Promise<{
    error: AuthError | Error | null,
    data: { user: SupabaseUser | null, session: Session | null } | null
  }> => {
    try {
      console.log('[AuthProvider] Starting signup process for:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        console.error('[AuthProvider] Auth signup error:', error);
        if (error) return {error, data};
      }

      console.log('[AuthProvider] Auth signup successful, user ID:', data.user?.id);

      if (data.user) {
        // Create user profile
        console.log('[AuthProvider] Creating user profile...');
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            phone_number: userData.phoneNumber,
            full_name: userData.fullName,
            timezone: userData.timezone,
          });

        if (profileError) {
          console.error('[AuthProvider] Error creating user profile:', profileError);
          return { error: profileError as Error, data };
        }
        console.log('[AuthProvider] User profile created successfully');

        // Create user preferences (use upsert to handle case where record already exists due to database trigger)
        console.log('[AuthProvider] Creating user preferences...');
        const { error: preferencesError } = await supabase
          .from('user_preferences')
          .upsert({
            user_id: data.user.id,
            morning_prompt_time: userData.morningTime,
            evening_checkin_time: userData.eveningTime,
            weekend_messaging: userData.weekendMessaging,
            response_preference: userData.responseMethod,
            goal_visibility: userData.goalVisibility,
          }, {
            onConflict: 'user_id'
          });

        if (preferencesError) {
          console.error('[AuthProvider] Error creating/updating user preferences:', preferencesError);
          // Don't fail the signup if preferences update fails, as the trigger may have already created the record
          if (preferencesError.code === '23505') {
            console.log('[AuthProvider] User preferences record already exists (likely created by trigger), continuing...');
          } else {
            return { error: preferencesError as Error, data };
          }
        } else {
          console.log('[AuthProvider] User preferences created/updated successfully');
        }

        // Create accountability partner if provided
        if (userData.hasAccountabilityPartner && userData.partnerEmail) {
          console.log('[AuthProvider] Creating accountability partner...');
          const { error: partnerError } = await supabase
            .from('accountability_partners')
            .insert({
              user_id: data.user.id,
              partner_email: userData.partnerEmail,
              partner_name: userData.partnerName,
              permission_confirmed: userData.hasPermission,
            });

          if (partnerError) {
            console.error('[AuthProvider] Error creating accountability partner:', partnerError);
          } else {
            console.log('[AuthProvider] Accountability partner created successfully');
          }
        }

        console.log('[AuthProvider] Signup process completed successfully');
      }

      return { error: null, data: data.user ? { user: data.user, session: data.session } : null };
    } catch (error) {
      console.error('[AuthProvider] Unexpected error in signup:', error);
      return { error: error as Error, data: null };
    }
  };

  const signIn = async (email: string, password: string): Promise<AuthErrorResponse> => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string): Promise<AuthErrorResponse> => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  };

  const updateUserProfile = async (updates: Partial<User>): Promise<AuthErrorResponse> => {
    if (!user) return { error: new Error('No user logged in') };

    const { error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', user.id);

    // If the update was successful, update the local state
    if (!error && userProfile) {
      setUserProfile({
        ...userProfile,
        ...updates,
        updated_at: new Date().toISOString()
      });
    }

    return { error };
  };

  const updateUserPreferences = async (updates: Partial<UserPreferences>, userId?: string): Promise<AuthErrorResponse> => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) return { error: new Error('No user logged in') };

    console.log('[AuthProvider] updateUserPreferences called for user:', targetUserId);

    // First, try to update the existing record
    const { error: updateError } = await supabase
      .from('user_preferences')
      .update(updates)
      .eq('user_id', targetUserId);

    if (updateError) {
      console.log('[AuthProvider] Update failed, error:', updateError);
      
      // If the record doesn't exist, try to create it
      if (updateError.code === 'PGRST116') {
        console.log('[AuthProvider] User preferences record not found, creating new record...');
        
        const { error: insertError } = await supabase
          .from('user_preferences')
          .insert({
            user_id: targetUserId,
            ...updates,
          });

        if (insertError) {
          console.error('[AuthProvider] Error creating user preferences:', insertError);
          return { error: insertError as Error };
        } else {
          console.log('[AuthProvider] User preferences created successfully');
        }
      } else {
        return { error: updateError as Error };
      }
    } else {
      console.log('[AuthProvider] User preferences updated successfully');
    }

    // If the update was successful and we're updating for the current user, update the local state
    if (userPreferences && targetUserId === user?.id) {
      setUserPreferences({
        ...userPreferences,
        ...updates,
        updated_at: new Date().toISOString()
      });
    }

    return { error: null };
  };

  const refreshUserData = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  const onboardUser = async (userId: string, phoneNumber: string) => {
    await fetch(`https://charlieosmond.app.n8n.cloud/webhook${import.meta.env.DEV ? "-test" : ""}/onboard-user`, {
      method: "POST",
      body: JSON.stringify({
        userId,
        phoneNumber,
      }),
    })
  }

  const value = {
    user,
    userProfile,
    userPreferences,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserProfile,
    updateUserPreferences,
    refreshUserData,
    onboardUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}